import dayjs from 'dayjs';
import omit from 'lodash/omit';
import values from 'lodash/values';
import { getQueryString } from 'api/utils';
import { convertRequestParams } from '@redux/crudCreator/dataProvider';
import { DynamicObjectType, FilterFromUrlResponseType } from 'types/commons';
import ButtonPrintLabel from '../components/ButtonPrintLabel';
import FilterLocation from '../components/FilterLocation';
import PriorityBatchButton from '../components/PriorityBatch';
import RangePickerFilter from '../components/RangePickerFilter';
import { newCheckRole } from 'utils/tools';

const PENDING_TYPE = {
  AUTOMATICALLY_PENDING: 'AUTOMATICALLY',
  MANUALLY_PENDING: 'MANUALLY',
  EXCHANGE_PENDING: 'EXCHANGE',
  PENDING_FOR_TAB_TRY_BUY: 'TAB_TRY_BUY',
};

const getCommonInitialFilter = (initialFilter, searchParams) => ({
  join: ['orderBatch', 'chainStore', 'shop', 'orderItems'],
  filters: [
    ...(initialFilter ? [initialFilter] : []),
    {
      condition: 'and',
      key: 'orderType',
      operator: 'ne',
      value: 'TAB_TRY_BUY',
    },
    searchParams.get('statusOms')
      ? {
          condition: 'and',
          key: 'statusOms',
          operator: 'is',
          value: searchParams.get('statusOms'),
        }
      : {},
  ],
  orderBy: 'createdAt',
});

const getRestAllOrdersInitialFilter = (commonFilter, filter) => ({
  ...commonFilter,
  filters: [
    ...values(commonFilter?.filters || {}),
    !filter?.filters?.find((item) => item.key === 'createdAt')
      ? {
          condition: 'and',
          key: 'createdAt',
          operator: 'gte',
          value: dayjs().subtract(1, 'month').startOf('month').toISOString(),
        }
      : {},
    !filter?.filters?.find((item) => item.key === 'createdAt')
      ? {
          condition: 'and',
          key: 'createdAt',
          operator: 'lte',
          value: dayjs().endOf('day').toISOString(),
        }
      : {},
  ],
});

const exportExcelQuery = (filterData) => {
  const requestParams = getQueryString(
    convertRequestParams(
      'GET_ALL',
      omit(filterData, ['join']) as DynamicObjectType,
    ),
  );
  return `/export-orders?${requestParams?.replace('page=1&limit=10', '')}`;
};

const hasExport = (roles, byPass) => {
  return newCheckRole('/export-orders', 'GET', roles, byPass) && true;
};

export const getListProps = (
  status,
  searchParams,
  filter,
  initialFilter,
  roles,
  byPass,
) => {
  const commonFilter: FilterFromUrlResponseType = getCommonInitialFilter(
    initialFilter,
    searchParams,
  );
  const restAllOrdersInitialFilter: FilterFromUrlResponseType =
    getRestAllOrdersInitialFilter(commonFilter, filter);

  const filterData = {
    filters: [
      ...values(restAllOrdersInitialFilter?.filters || {}),
      ...values(filter.filters || {}),
    ],
    orderBy: 'createdAt',
  };

  const defaultProps = {
    initialFilter: commonFilter,
    model: 'orders',
    rowSelectionActions: <></>,
    hasImport: false,
    hasExport: false,
    extraActionFilters: [<RangePickerFilter />],
  };

  switch (status) {
    case 'AWAITING_CONFIRMATION':
    case 'CONFIRMED':
    case 'READY_TO_HANDOVER':
    case 'DELIVERING':
    case 'FAILED_DELIVERY':
    case 'RETURNING':
    case 'LOST':
    case 'REJECTED':
    case 'FAIL_QC':
      return defaultProps;
    case 'PICKING':
      return {
        ...defaultProps,
        initialFilter: {
          ...commonFilter,
          filters: [...(initialFilter ? [initialFilter] : [])],
        },
        model: 'orderBatchesPicking',
        defaultOptions: {
          customApiResource: `order-batches/custom-views-picking`,
        },
        extraActions: [<PriorityBatchButton />],
      };
    case 'PACKING':
      return {
        ...defaultProps,
        initialFilter: {
          ...commonFilter,
          filters: [
            ...(initialFilter ? [initialFilter] : []),
            searchParams.get('statusOms')
              ? {
                  condition: 'and',
                  key: 'batchStatus',
                  operator: 'is',
                  value: ['IN_PROGRESS', 'COMPLETED'],
                }
              : {},
          ],
        },
        model: 'orderBatches',
        defaultOptions: {
          customApiResource: `order-batches/custom-views-packing`,
        },
        extraActionFilters: [
          ...(defaultProps.extraActionFilters || []),
          newCheckRole(
            '/label/batch/:batchId/bulk-print-marketplace',
            'POST',
            roles,
            byPass,
          ) && <ButtonPrintLabel />,
        ],
      };
    case 'READY_TO_PICK':
      return {
        ...defaultProps,
        initialFilter: {
          ...commonFilter,
          pageSize: 100,
        },
        model: 'ordersReadyToPick',
        extraActionFilters: [
          ...(defaultProps.extraActionFilters || []),
          <FilterLocation />,
        ],
      };
    case 'MANUALLY_PENDING':
    case 'AUTOMATICALLY_PENDING':
    case 'EXCHANGE_PENDING':
      return {
        ...defaultProps,
        initialFilter: {
          ...commonFilter,
          filters: [
            ...(initialFilter ? [initialFilter] : []),
            {
              condition: 'and',
              key: 'pendingType',
              operator: 'is',
              value: PENDING_TYPE[status],
            },
            searchParams.get('statusOms')
              ? {
                  condition: 'and',
                  key: 'statusOms',
                  operator: 'is',
                  value: 'PENDING_PROCESSING',
                }
              : {},
          ],
        },
      };
    default:
      return {
        ...defaultProps,
        initialFilter: restAllOrdersInitialFilter,
        hasExport: hasExport(roles, byPass),
        extraActionFilters: <RangePickerFilter />,
        exportExcelQuery: `${exportExcelQuery(filterData)}`,
        excelName: `Export_orders_${dayjs().format('YYYY_MM_DD')}`,
      };
  }
};
